<script setup lang="ts">
/**
 * FormFieldSelect is a shorthand helper for creating a dropdown/select input field in a form.
 * It wraps our usual Form components, but providing a shorter API to them.
 *
 * Each option should be of the shape { title: string, value: string }.
 * The component accepts an array of options, or an array of arrays of options.
 * Using a nested array will create groups of options, that will have separators between them.
 */

import { computed } from 'vue';
import { vAutoAnimate } from '@formkit/auto-animate/vue';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../form-dependencies/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../form-dependencies/select';

defineOptions({
  inheritAttrs: false,
});

interface Option {
  value: string;
  title: string;
}

const props = defineProps<{
  name: string;
  options?: Array<Array<Option>> | Array<Option>;
  description?: string;
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
}>();

const options = computed(() => {
  if (!props.options) {
    return [[]];
  }

  // Options can be grouped, but the prop accepts an array of ungrouped options. If we get this, put it in a single group (put it all in an array).
  return (Array.isArray(props.options[0]) ? props.options : [props.options]);
});

const canRender = computed(() => !options.value.some(item => !Array.isArray(item)));
</script>

<template>
  <FormField v-if="canRender" v-slot="{ componentField }" :name="props.name">
    <FormItem :id="props.id || props.name" v-auto-animate>
      <FormLabel>
        <template v-if="!$slots.default">
          {{ props.label }}<span v-if="props.required" class="label--required text-[color:var(--theme-highlight-color)]"> *</span>
        </template>
      </FormLabel>

      <Select v-bind="{ ...componentField, ...$attrs }">
        <FormControl>
          <SelectTrigger>
            <SelectValue :placeholder="props.placeholder" />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          <template v-if="!$slots.options">
            <SelectGroup v-for="(optionGroup, index) in options as Array<Array<Option>>" :key="index">
              <SelectItem v-for="option in optionGroup" :key="option.value" :value="option.value">
                {{ option.title }}
              </SelectItem>
            </SelectGroup>
          </template>
          <slot name="options" />
        </SelectContent>
      </Select>

      <FormDescription v-if="props.description || $slots.description">
        <template v-if="!$slots.description">
          {{ props.description }}
        </template>
        <slot name="description" />
      </FormDescription>

      <FormMessage />
    </FormItem>
  </FormField>
</template>
